import { Seo, StructuredData } from '@/components/molecules';
import { ImportPromiseBand, PageIntro, QuoteBand } from '@/components/organisms';
import { type WhatsAppRouteHandle } from '@/hooks/usePageWhatsAppMessage';
import { breadcrumbSchema } from '@/utils/structuredData';

import { crumbs, WHATSAPP_MESSAGE } from './content';
import { useDeliveryIntroHighlights } from './hooks/useDeliveryIntroHighlights';
import {
  DeliveryGalleryBridge,
  DeliveryRoutes,
  HandoverTimeline,
  RegionalCoverageBand,
} from './organisms';

/** Opens WhatsApp with the context of this page (§60 K/L). */
export const handle: WhatsAppRouteHandle = { whatsappMessage: WHATSAPP_MESSAGE };

/**
 * Destinations (§2).
 *
 * Ordered from the primary Kampala route through named border points and
 * regional enquiry coverage, then the handover process and the delivered-car
 * archive. The route pricing remains data-driven; wider destinations are
 * enquiry-led and do not inherit a Mombasa tariff.
 *
 * The page composes and nothing else. Both bands are two-column layouts with
 * sticky heading columns, so each owns its own `Section` and its own heading —
 * a page that stacked `SectionHeading`s above them would be deciding their
 * internal proportions from the outside. The one hook here feeds the intro
 * strip, whose labels are counted off the delivery routes rather than written
 * down twice.
 */
export default function DestinationsPage() {
  const highlights = useDeliveryIntroHighlights();

  return (
    <>
      <StructuredData data={[breadcrumbSchema([{ label: 'Home', to: '/' }, ...crumbs])]} />
      <Seo
        title="Destinations"
        description="Vehicle delivery options for Kampala, Bunagana, Nimule and Katuna, with enquiry-based destination planning across East Africa and Africa."
        path="/destinations"
      />

      <PageIntro
        eyebrow="Destinations"
        title="Your car, delivered closer to home"
        lede="Choose a named route or ask us about destinations across East Africa and beyond. We’ll discuss the right delivery options for your vehicle and order."
        crumbs={crumbs}
        slideshow
        highlights={highlights}
        whatsapp
      />

      <DeliveryRoutes />

      <RegionalCoverageBand />

      <HandoverTimeline />

      <DeliveryGalleryBridge />

      <ImportPromiseBand />
      <QuoteBand title="Ready to start?" whatsappMessage={WHATSAPP_MESSAGE} />
    </>
  );
}
